import {
	Area,
	Color,
	Degrees,
	DegreesPerMillisecond,
	Millisecond,
	NumberOfPixels,
	Opacity,
	OpacityPerMillisecond,
	PixelsPerMillisecond,
	Rectangle,
	Scale,
	ScalePerMillisecond,
} from '@mos/client/shared/utils'
import { clamp, randomInRange } from '@mos/shared/utils'
import { EmitterConfig } from './emitter.config'

export class Particle {
	public fadeInDuration: Millisecond
	public falloffDistance: NumberOfPixels
	public falloffExpire: Millisecond
	public displayArea: Area // Set here to avoid passing the component to the config object
	public spawnArea: Rectangle

	public x: NumberOfPixels
	public y: NumberOfPixels
	public rotation: Degrees
	public image: HTMLImageElement | null
	public size: NumberOfPixels
	public scaleX: Scale
	public scaleY: Scale
	public opacity: Opacity
	public startTime: Millisecond
	public age: number // Percentage of life elapsed (0-1+)
	public ttl: Millisecond
	public tint: Color
	public velocityAlpha: OpacityPerMillisecond
	public velocityX: PixelsPerMillisecond
	public velocityY: PixelsPerMillisecond
	public velocityRotation: DegreesPerMillisecond
	public velocityScaleX: ScalePerMillisecond
	public velocityScaleY: ScalePerMillisecond
	private initialAlpha: Opacity

	// Static image cache to avoid loading the same image multiple times
	static images: Record<string, HTMLImageElement> = {}

	constructor(private config: EmitterConfig) {
		// Order matters for what is accessible in the config object
		this.displayArea = config.displayArea
		this.falloffDistance = config.falloffDistance ?? 5
		this.falloffExpire = config.falloffExpire ?? 200
		this.fadeInDuration = config.fadeInDuration ?? 200

		const spawnAreas = this.config.spawnArea?.(this) ?? [
			{
				x: 0,
				y: 0,
				width: config.displayArea.width,
				height: config.displayArea.height,
			},
		]
		this.spawnArea = Array.isArray(spawnAreas) ? this.getWeightedSpawnArea(spawnAreas) : spawnAreas

		this.image = null
		if (config.image) {
			if (Particle.images[config.image]) {
				this.image = Particle.images[config.image]
			} else {
				this.image = new Image()
				this.image.src = config.image
				Particle.images[config.image] = this.image
			}
		}
		this.size = config.size ?? 1

		this.startTime = Date.now()
		this.age = 0

		this.x = randomInRange(this.spawnArea.x, this.spawnArea.x + this.spawnArea.width)
		this.y = randomInRange(this.spawnArea.y, this.spawnArea.y + this.spawnArea.height)
		this.initialAlpha = config.initialAlpha?.(this) ?? 1
		this.opacity = 0
		this.rotation = config.initialRotation?.(this) ?? 0
		this.scaleX = config.initialScaleX?.(this) ?? 1
		this.scaleY = config.initialScaleY?.(this) ?? 1
		this.ttl = config.ttl?.(this) ?? randomInRange(500, 1500)
		this.tint = config.tint?.(this) ?? '#FFFFFF'
		this.velocityAlpha = config.velocityAlpha?.(this) ?? 0
		this.velocityX = config.velocityX?.(this) ?? 0
		this.velocityY = config.velocityY?.(this) ?? 0
		this.velocityRotation = config.velocityRotation?.(this) ?? 0
		this.velocityScaleX = config.velocityScaleX?.(this) ?? 0
		this.velocityScaleY = config.velocityScaleY?.(this) ?? 0
	}

	update(delta: number) {
		const elapsed = Date.now() - this.startTime
		const remaining = this.ttl - elapsed
		this.age = elapsed / this.ttl

		// Handle opacity growth in the fadeInDuration
		if (elapsed < this.fadeInDuration) {
			this.opacity = clamp((elapsed / this.fadeInDuration) * this.initialAlpha, 0, this.initialAlpha)
		} else {
			this.opacity = clamp(this.opacity - this.velocityAlpha * delta, 0, this.initialAlpha)
		}

		this.x += this.velocityX * delta
		this.y += this.velocityY * delta
		this.rotation += this.velocityRotation * delta
		this.scaleX += this.velocityScaleX * delta
		this.scaleY += this.velocityScaleY * delta

		// Ensure the opacity decreases as the particle approaches the end of its life.
		if (remaining < this.falloffExpire && remaining / this.falloffExpire < this.opacity) {
			this.opacity = clamp(remaining / this.falloffExpire, 0, 1)
		}

		// Adjust opacity based on distance from edges
		const halfSizeX = (this.size * this.scaleX) / 2
		const halfSizeY = (this.size * this.scaleY) / 2

		const distanceToLeftEdge = this.x
		const distanceToRightEdge = this.config.displayArea.width - this.x
		const distanceToTopEdge = this.y
		const distanceToBottomEdge = this.config.displayArea.height - this.y

		if (distanceToLeftEdge < this.falloffDistance + halfSizeX) {
			this.opacity = Math.min(this.opacity, clamp((distanceToLeftEdge - halfSizeX) / this.falloffDistance, 0, 1))
		}
		if (distanceToRightEdge < this.falloffDistance + halfSizeX) {
			this.opacity = Math.min(this.opacity, clamp((distanceToRightEdge - halfSizeX) / this.falloffDistance, 0, 1))
		}
		if (distanceToTopEdge < this.falloffDistance + halfSizeY) {
			this.opacity = Math.min(this.opacity, clamp((distanceToTopEdge - halfSizeY) / this.falloffDistance, 0, 1))
		}
		if (distanceToBottomEdge < this.falloffDistance + halfSizeY) {
			this.opacity = Math.min(
				this.opacity,
				clamp((distanceToBottomEdge - halfSizeY) / this.falloffDistance, 0, 1),
			)
		}
	}

	draw(ctx: CanvasRenderingContext2D) {
		ctx.save()
		ctx.globalAlpha = this.opacity
		ctx.translate(this.x, this.y)
		ctx.rotate((this.rotation * Math.PI) / 180)
		ctx.scale(this.scaleX, this.scaleY)

		const image = this.image
		if (image !== null) {
			// Skip frames where the image hasn't finished loading yet, or failed to load -
			// drawImage throws InvalidStateError on a "broken" image (MOS-WEB-29).
			if (image.complete && image.naturalWidth > 0) {
				ctx.drawImage(image, -this.size / 2, -this.size / 2, this.size, this.size)
				ctx.globalCompositeOperation = 'source-atop'
				ctx.fillStyle = this.tint
				ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size)
				ctx.globalCompositeOperation = 'source-over'
			}
		} else {
			// FYI : I tried to use a gradient but performance was terrible on Firefox mobile. Using an image is better.

			ctx.beginPath()
			ctx.arc(0, 0, this.size, 0, Math.PI * 2)
			ctx.fillStyle = 'white'
			ctx.fill()
			ctx.globalCompositeOperation = 'source-atop'
			ctx.fillStyle = this.tint
			ctx.fill()
			ctx.globalCompositeOperation = 'source-over'
		}

		ctx.restore()
	}

	// Note: update() must be called before isExpired()
	isExpired() {
		return this.age >= 1
	}

	private getWeightedSpawnArea(spawnAreas: Rectangle[]): Rectangle {
		const totalSurface = spawnAreas.reduce((sum, area) => sum + area.width * area.height, 0)
		const cumulativeProbabilities: number[] = []
		let cumulativeSum = 0

		for (const area of spawnAreas) {
			cumulativeSum += (area.width * area.height) / totalSurface
			cumulativeProbabilities.push(cumulativeSum)
		}

		const randomValue = Math.random()
		for (let i = 0; i < cumulativeProbabilities.length; i++) {
			if (randomValue < cumulativeProbabilities[i]) {
				return spawnAreas[i]
			}
		}
		return spawnAreas[spawnAreas.length - 1]
	}
}
